import type React from "react";
import { useEffect, useRef, useState } from "react";

interface WebCamProps {
  mini?: boolean;
  scale?: number;
  candidateName?: string;
  stream?: MediaStream | null;
  variant?: 'standard' | 'round';
}

const WebCam: React.FC<WebCamProps> = ({ mini = false, scale = 1, candidateName, stream: externalStream = null, variant = 'standard' }) => {
  const userName = candidateName?.trim() || "Candidate";
  const videoRef = useRef<HTMLVideoElement>(null);
  const [internalStream, setInternalStream] = useState<MediaStream | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let currentStream: MediaStream | null = null;

    const requestStream = async () => {
      if (externalStream) {
        setIsLoading(false);
        return;
      }
      const storedCamera = localStorage.getItem("selectedCamera");
      const constraintsList: MediaStreamConstraints[] = storedCamera
        ? [{ video: { deviceId: { exact: storedCamera } } }, { video: true }]
        : [{ video: true }];

      setIsLoading(true);

      for (const constraints of constraintsList) {
        try {
          const camStream = await navigator.mediaDevices.getUserMedia(constraints);
          if (!active) {
            camStream.getTracks().forEach((track) => track.stop());
            return;
          }
          currentStream = camStream;
          setInternalStream(camStream);
          return;
        } catch (error) {
          // ignore constraint failures and try next option
        }
      }

      setInternalStream(null);
      setIsLoading(false);
    };

    requestStream().finally(() => {
      if (active) {
        setIsLoading(false);
      }
    });

    return () => {
      active = false;
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      if (currentStream) {
        currentStream.getTracks().forEach((track) => track.stop());
      }
      setInternalStream(null);
    };
  }, [externalStream]);

  useEffect(() => {
    if (!videoRef.current) return;
    const stream = externalStream ?? internalStream;
    if (stream) {
      videoRef.current.srcObject = stream;
      // Avoid unhandled promise rejection if autoplay is blocked
      const playPromise = videoRef.current.play();
      if (playPromise?.catch) {
        playPromise.catch(() => undefined);
      }
    } else {
      videoRef.current.srcObject = null;
    }
  }, [externalStream, internalStream]);

  const stream = externalStream ?? internalStream;

  if (mini) {
    return (
      <DraggableWebcam
        userName={userName}
        scale={scale}
        stream={stream}
        videoRef={videoRef}
        isLoading={isLoading}
      />
    );
  }

  if (variant === 'round') {
    return (
      <RoundWebcam
        userName={userName}
        scale={scale}
        stream={stream}
        videoRef={videoRef}
        isLoading={isLoading}
      />
    );
  }

  return (
    <StandardWebcam
      userName={userName}
      scale={scale}
      stream={stream}
      videoRef={videoRef}
      isLoading={isLoading}
    />
  );
};

const RoundWebcam: React.FC<{
  userName?: string;
  scale?: number;
  stream: MediaStream | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isLoading: boolean;
}> = ({ scale = 1, stream, videoRef, isLoading }) => {
  return (
    <div className="relative w-full aspect-square overflow-hidden rounded-full bg-slate-100 border border-slate-200">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="w-full h-full object-cover"
        style={{ transform: `scaleX(-1) scale(${scale})`, transition: "transform 0.3s ease" }}
      />
      {!stream && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-[10px] text-center px-2">
          Camera unavailable
        </div>
      )}
    </div>
  );
};

const DraggableWebcam: React.FC<{
  userName?: string;
  scale?: number;
  stream: MediaStream | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isLoading: boolean;
}> = ({ userName, scale = 1, stream, videoRef, isLoading }) => {
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: -1, y: -1 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isMouseOverCircle, setIsMouseOverCircle] = useState(false);
  const webcamContainerRef = useRef<HTMLDivElement>(null);

  const isPointInCircle = (clientX: number, clientY: number) => {
    if (!webcamContainerRef.current) return false;

    const rect = webcamContainerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const radius = rect.width / 2;
    const distance = Math.sqrt((clientX - centerX) ** 2 + (clientY - centerY) ** 2);
    return distance <= radius;
  };

  const handleMouseOver = (event: React.MouseEvent) => {
    setIsMouseOverCircle(isPointInCircle(event.clientX, event.clientY));
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    if (!webcamContainerRef.current) return;
    if (isPointInCircle(event.clientX, event.clientY)) {
      setIsDragging(true);
      const rect = webcamContainerRef.current.getBoundingClientRect();
      setDragOffset({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
      event.preventDefault();
    }
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    if (!webcamContainerRef.current || event.touches.length === 0) return;
    const touch = event.touches[0];
    if (!touch) return;

    if (isPointInCircle(touch.clientX, touch.clientY)) {
      setIsDragging(true);
      const rect = webcamContainerRef.current.getBoundingClientRect();
      setDragOffset({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      });
    }
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (!isDragging || !webcamContainerRef.current) return;

    const parent = webcamContainerRef.current.parentElement;
    const maxX = parent ? parent.offsetWidth - webcamContainerRef.current.offsetWidth : window.innerWidth - webcamContainerRef.current.offsetWidth;
    const maxY = parent ? parent.offsetHeight - webcamContainerRef.current.offsetHeight : window.innerHeight - webcamContainerRef.current.offsetHeight;

    const parentRect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };
    const newX = event.clientX - dragOffset.x - parentRect.left;
    const newY = event.clientY - dragOffset.y - parentRect.top;

    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  };

  const handleTouchMove = (event: TouchEvent) => {
    if (!isDragging || !webcamContainerRef.current || event.touches.length === 0) return;

    const touch = event.touches[0];
    if (!touch) return;

    const parent = webcamContainerRef.current.parentElement;
    const maxX = parent ? parent.offsetWidth - webcamContainerRef.current.offsetWidth : window.innerWidth - webcamContainerRef.current.offsetWidth;
    const maxY = parent ? parent.offsetHeight - webcamContainerRef.current.offsetHeight : window.innerHeight - webcamContainerRef.current.offsetHeight;

    const parentRect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };
    const newX = touch.clientX - dragOffset.x - parentRect.left;
    const newY = touch.clientY - dragOffset.y - parentRect.top;

    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });

    event.preventDefault();
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, dragOffset]);

  const getCursorStyle = () => {
    if (!isMouseOverCircle) return "default";
    return isDragging ? "grabbing" : "grab";
  };

  return (
    <div
      ref={webcamContainerRef}
      style={{
        position: "absolute",
        left: position.x === -1 ? undefined : `${position.x}px`,
        top: position.y === -1 ? undefined : `${position.y}px`,
        right: position.x === -1 ? 0 : undefined,
        bottom: position.y === -1 ? 0 : undefined,
        zIndex: 1000,
        touchAction: "none",
        cursor: getCursorStyle(),
        overflow: "hidden",
        borderRadius: "100%",
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onMouseMove={handleMouseOver}
      onMouseLeave={() => setIsMouseOverCircle(false)}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="w-52 h-52 rounded-full object-cover shadow-md border border-gray-300"
        style={{ transform: `scaleX(-1) scale(${scale})`, transition: "transform 0.3s ease" }}
      />
      {!stream && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/60 text-white text-sm">
          Camera unavailable
        </div>
      )}
    </div>
  );
};

const StandardWebcam: React.FC<{
  userName?: string;
  scale?: number;
  stream: MediaStream | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isLoading: boolean;
}> = ({ userName, scale = 1, stream, videoRef, isLoading }) => {
  const displayName = userName?.trim() || "Candidate";

  return (
    <div className="relative w-full max-w-full h-full overflow-hidden rounded-xl bg-black">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="w-full h-full object-cover"
        style={{ transform: `scaleX(-1) scale(${scale})`, transition: "transform 0.3s ease" }}
      />
      {!stream && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-sm">
          Turn on your camera to see the preview
        </div>
      )}
      <div className="absolute bottom-4 left-4 md:bottom-2 md:left-2 lg:bottom-2.5 lg:left-2.5 xl:bottom-3 xl:left-3 bg-white/90 px-4 py-2 md:px-2 md:py-0 lg:px-2 lg:py-0.5 xl:px-2.5 xl:py-1.5 rounded-xl shadow-sm">
        <p className="text-sm md:text-[8px] lg:text-[10px] xl:text-[13px] font-medium text-slate-900">{displayName}</p>
      </div>
    </div>
  );
};

export default WebCam;
